import React, { Component } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { createResourceLocatorString } from '../../../util/routes';
import { getSearchPageResourceLocatorStringParams } from '../SearchPage.shared';

import { ModalInMobile } from '../../../components';

import css from './SearchFiltersMobile.module.css';

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const FilterIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M3 5h14M5 10h10M8 15h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

class SearchFiltersMobileComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { isFiltersOpenOnMobile: false, initialQueryParams: null };
    this.filterTriggerRef = React.createRef();
    this.dialogRef = React.createRef();
    this.closeButtonRef = React.createRef();
    this.focusFrame = null;

    this.openFilters = this.openFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.closeFilters = this.closeFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
  }

  componentWillUnmount() {
    this.cancelPendingFocus();
  }

  cancelPendingFocus() {
    if (this.focusFrame !== null) {
      window.cancelAnimationFrame(this.focusFrame);
      this.focusFrame = null;
    }
  }

  // Open filters modal, set the initial parameters to current ones
  openFilters() {
    const { onOpenModal, urlQueryParams } = this.props;
    onOpenModal();
    this.setState({ isFiltersOpenOnMobile: true, initialQueryParams: urlQueryParams }, () => {
      // ModalInMobile removes its hidden state in a nested update. Wait until
      // that update has committed before focusing a control inside it.
      this.cancelPendingFocus();
      this.focusFrame = window.requestAnimationFrame(() => {
        this.focusFrame = null;
        if (this.state.isFiltersOpenOnMobile) this.closeButtonRef.current?.focus();
      });
    });
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    this.cancelPendingFocus();
    const { history, onCloseModal, location, routeConfiguration } = this.props;

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(
        routeName,
        routeConfiguration,
        pathParams,
        this.state.initialQueryParams
      )
    );
    onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false, initialQueryParams: null }, () => {
      this.filterTriggerRef.current?.focus();
    });
  }

  // Close the filter modal
  closeFilters() {
    this.cancelPendingFocus();
    this.props.onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false }, () => {
      this.filterTriggerRef.current?.focus();
    });
  }

  // Keep keyboard navigation inside this in-place mobile dialog.
  handleDialogKeyDown = event => {
    if (event.key !== 'Tab' || !this.state.isFiltersOpenOnMobile) return;
    const controls = Array.from(
      this.dialogRef.current?.querySelectorAll(
        'button:not(:disabled), input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]'
      ) || []
    ).filter(element => !element.closest('[hidden], [aria-hidden="true"]'));
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  // Reset all filter query parameters
  resetAll(e) {
    this.props.resetAll(e);
  }

  render() {
    const {
      rootClassName,
      className,
      children,
      sortByComponent,
      listingsAreLoaded,
      resultsCount,
      searchInProgress = false,
      searchListingsError,
      showAsModalMaxWidth,
      onMapIconClick = () => {},
      onManageDisableScrolling,
      selectedFiltersCount = 0,
      noResultsInfo,
      intl,
      isMapVariant = true,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    const resultsFound = (
      <FormattedMessage id="SearchFiltersMobile.foundResults" values={{ count: resultsCount }} />
    );
    const noResults = <FormattedMessage id="SearchFiltersMobile.noResults" />;
    const loadingResults = <FormattedMessage id="SearchFiltersMobile.loadingResults" />;
    const modalCloseButtonMessage = intl.formatMessage({ id: 'SearchFiltersMobile.closeFilters' });
    const isLoading = searchInProgress || (!listingsAreLoaded && !searchListingsError);
    const hasResults = Number.isFinite(resultsCount) && resultsCount > 0;
    const ctaLabel = intl.formatMessage(
      {
        id: isLoading
          ? 'SearchFiltersMobile.loadingResults'
          : searchListingsError
          ? 'SearchFiltersMobile.backToResults'
          : hasResults
          ? 'SearchFiltersMobile.showListings'
          : 'SearchFiltersMobile.noResults',
      },
      { count: resultsCount }
    );
    const isCtaDisabled = isLoading || (!searchListingsError && !hasResults);

    return (
      <div className={classes}>
        <div className={css.searchResultSummary}>
          {!isLoading && !searchListingsError && listingsAreLoaded && resultsCount > 0
            ? resultsFound
            : null}
          {!isLoading && !searchListingsError && listingsAreLoaded && resultsCount === 0
            ? noResults
            : null}
          {isLoading ? loadingResults : null}
        </div>
        <div className={css.buttons}>
          <button
            type="button"
            className={classNames(css.toolbarButton, {
              [css.toolbarButtonActive]: selectedFiltersCount > 0,
            })}
            ref={this.filterTriggerRef}
            aria-haspopup="dialog"
            aria-expanded={this.state.isFiltersOpenOnMobile}
            aria-controls={
              this.state.isFiltersOpenOnMobile ? 'SearchFiltersMobile.dialog' : undefined
            }
            onClick={this.openFilters}
          >
            <FilterIcon />
            <FormattedMessage id="SearchFiltersMobile.filtersButtonLabel" />
          </button>

          {sortByComponent}
          {isMapVariant ? (
            <div className={css.mapIcon} onClick={onMapIconClick}>
              <FormattedMessage id="SearchFiltersMobile.openMapView" className={css.mapIconText} />
            </div>
          ) : null}
        </div>

        {noResultsInfo ? noResultsInfo : null}

        <ModalInMobile
          id="SearchFiltersMobile.filters"
          className={css.filterModalRoot}
          isModalOpenOnMobile={this.state.isFiltersOpenOnMobile}
          onClose={this.closeFilters}
          showAsModalMaxWidth={showAsModalMaxWidth}
          onManageDisableScrolling={onManageDisableScrolling}
          containerClassName={css.modalContainer}
          closeButtonMessage={modalCloseButtonMessage}
        >
          <div
            id="SearchFiltersMobile.dialog"
            className={css.dialog}
            ref={this.dialogRef}
            role={this.state.isFiltersOpenOnMobile ? 'dialog' : undefined}
            aria-modal={this.state.isFiltersOpenOnMobile ? true : undefined}
            aria-label={intl.formatMessage({ id: 'SearchFiltersMobile.heading' })}
            onKeyDown={this.handleDialogKeyDown}
          >
            <div className={css.stickyHeader}>
              <button
                type="button"
                className={css.resultsCta}
                onClick={this.closeFilters}
                disabled={isCtaDisabled}
              >
                {ctaLabel}
              </button>
              <button
                type="button"
                ref={this.closeButtonRef}
                className={css.closeX}
                onClick={this.closeFilters}
                aria-label={modalCloseButtonMessage}
              >
                <CloseIcon />
              </button>
            </div>
            {this.state.isFiltersOpenOnMobile ? (
              <div className={css.filtersWrapper}>
                {searchListingsError && !isLoading ? (
                  <p role="alert">
                    <FormattedMessage id="SearchPage.searchError" />
                  </p>
                ) : null}
                {children}
                <div className={css.clearAllZone}>
                  <button
                    type="button"
                    className={css.clearAllButton}
                    onClick={e => this.resetAll(e)}
                  >
                    <FormattedMessage id="SearchFiltersMobile.resetAll" />
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </ModalInMobile>
      </div>
    );
  }
}

/**
 * SearchFiltersMobile component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Object} props.urlQueryParams - The URL query params
 * @param {React.Node} props.sortByComponent - The sort by component
 * @param {boolean} props.listingsAreLoaded - Whether the listings are loaded
 * @param {number} props.resultsCount - The number of results
 * @param {boolean} props.searchInProgress - Whether the search is in progress
 * @param {number} props.showAsModalMaxWidth - The maximum width of the modal
 * @param {Function} props.onMapIconClick - The function to click the map icon
 * @param {Function} props.onManageDisableScrolling - The function to manage disable scrolling
 * @param {Function} props.onOpenModal - The function to open the modal
 * @param {Function} props.onCloseModal - The function to close the modal
 * @param {Function} props.resetAll - The function to reset all
 * @param {number} props.selectedFiltersCount - The number of selected filters
 * @param {boolean} props.isMapVariant - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchFiltersMobile = props => {
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();

  return (
    <SearchFiltersMobileComponent
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      {...props}
    />
  );
};

export default SearchFiltersMobile;
